import { z } from 'zod';
import { ulidSchema } from './id';

/**
 * Per-member board view state — block B40.
 *
 * These contracts cover the server-owned half of `FR-VIEW-003` and
 * `FR-VIEW-008`/`FR-BOARD-018`: remembering one member's last board view and
 * one member's collapsed lists. The state is private to the caller, emits no
 * activity event, and is deliberately not folded into `boardContentsSchema`.
 *
 * ⚠ `BOARD_VIEW_KINDS` is the vocabulary shared by `board_view_preferences`
 * and `board_pinned_views`; do not reuse it for `workspace_views.view_kind`,
 * whose live CHECK is narrower (`table`, `calendar` only).
 */

export const BOARD_VIEW_KINDS = [
  'board',
  'table',
  'calendar',
  'timeline',
  'dashboard',
  'map',
] as const;

export const boardViewKindSchema = z.enum(BOARD_VIEW_KINDS);

export type BoardViewKind = z.infer<typeof boardViewKindSchema>;

export const boardViewPreferenceSchema = z.strictObject({
  lastView: boardViewKindSchema,
});

export type BoardViewPreference = z.infer<typeof boardViewPreferenceSchema>;

export const listCollapseStateSchema = z.object({
  listId: ulidSchema,
  collapsed: z.boolean(),
});

export type ListCollapseState = z.infer<typeof listCollapseStateSchema>;

/**
 * A set of collapsed list ids for the caller on one board. The ids are returned
 * in board-face list order only for determinism; clients must treat this as a
 * membership set to zip against `GET /boards/:id/contents`, not as a render
 * order.
 */
export const boardCollapsedListsSchema = z.object({
  listIds: z.array(ulidSchema),
});

export type BoardCollapsedLists = z.infer<typeof boardCollapsedListsSchema>;

/**
 * ── The per-member DISPLAY state, and why it is not board-scoped ───────────
 *
 * Focus mode — `FR-VIEW-011`. **A display state, not a zoom control**: the
 * requirement's own behaviour text says "There is no board-content zoom
 * control: magnification is the browser's or OS's, not the product's", and
 * nothing behind these schemas scales, transforms or magnifies anything. It
 * turns chrome off, and that is the whole of it.
 *
 * ⚠ **User-scoped, with no `boardId` anywhere in the contract — because the
 * live catalogue settled it.** `user_preferences.focus_mode_enabled` is a
 * `boolean NOT NULL DEFAULT false` on a table whose primary key is `user_id`
 * alone; there is no `board_id` column and this chunk writes no migration to
 * add one. The frontend's shipped `localStorage` key
 * (`molt:board-view-chrome:<userId>:<boardId>`, above) scopes it
 * user-AND-board, and those two cannot both be right.
 *
 * The column wins, and `FR-VIEW-011` AC2 is the reason it is allowed to:
 * *"when the member reloads the board, then focus mode is still on **for that
 * member**"* — for that member, not for that board. The requirement is
 * indifferent to board scope; the schema is not.
 *
 * **So a member who turns focus mode on for one board and opens another finds
 * it on there too.** That is the contract, deliberately. Focus mode is a
 * property of how this member wants the product to look while presenting, not
 * a property of a board — which is also what makes AC3 free: the row is keyed
 * by `user_id`, so another member opening the same board reads their own row
 * and their chrome is untouched.
 *
 * ⚠ Do not confuse this with `boardViewPreferenceSchema` above. That one is
 * board-scoped (`GET/PUT /boards/:id/view`, one row per `(user_id, board_id)`)
 * and remembers *which view*; this one is user-scoped and remembers *whether
 * the chrome is hidden*. The paths are deliberately spelled differently —
 * `display`, which is `FR-VIEW-011`'s own word, against `view` — so that a
 * reader who meets both does not have to work out which is which.
 */

/**
 * `GET /me/display-preferences` and the 200 of every patch below.
 *
 * A plain `z.object`, like other shared response views: a client that meets a
 * field a later block adds should ignore it, not fail. No row in
 * `user_preferences` yields the database default rather than a 404 — *absent*
 * and *false* are the same answer for a `NOT NULL DEFAULT false` column, so
 * there is no tri-state to represent and nothing for `null` to mean.
 */
export const displayPreferencesSchema = z.object({
  focusModeEnabled: z.boolean(),
});

export type DisplayPreferences = z.infer<typeof displayPreferencesSchema>;

/**
 * `PATCH /me/display-preferences/focus-mode` — field-scoped per SC-4.
 *
 * `strictObject`, matching `singleKeyShortcutsPatchSchema` and its siblings:
 * this route may touch exactly one column, so an unrelated preference key must
 * be a validation failure rather than a silently stripped field. Both
 * directions are legal — focus mode "reverses with the same control", in
 * `FR-VIEW-011`'s words, so `false` is as much a real request as `true`.
 */
export const focusModePatchSchema = z.strictObject({
  focusModeEnabled: z.boolean(),
});

export type FocusModePatch = z.infer<typeof focusModePatchSchema>;
